import { useMemo, useState } from 'react';
import { ArrowRight, History, Pencil, Plus, ReceiptText, Store } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import { Modal } from '@/components/ui/modal';
import { apiErrorMessage } from '@/lib/api-error';
import { cn, formatDateTime } from '@/lib/utils';
import { toast } from '@/stores/toast-store';
import {
  useAdminTenantFeeHistory,
  useAdminTenantFees,
  useSaveTenantFeeChanges,
  type FeeCalculationMethod,
  type TenantFee,
  type TenantFeeChange,
} from '@/hooks/use-admin-tenant-fees';

// ── Formatting ────────────────────────────────────────────────────────────────

// Same limits as the column (numeric(10, 4)) and the API's validation.
const AMOUNT_RE = /^\d{1,6}(\.\d{1,4})?$/;

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});
const plain = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/** Fees carry up to 4 decimals ($0.008 per SMS segment), so cents-only formatting would misstate them. */
function formatRate(amount: string, method: FeeCalculationMethod): string {
  const n = Number(amount);
  return method === 'PERCENTAGE' ? `${plain.format(n)}%` : money.format(n);
}

/** '0.2500' → '0.25', '0.0080' → '0.008' — what goes in an input. */
function inputAmount(amount: string): string {
  return plain.format(Number(amount)).replace(/,/g, '');
}

function sameAmount(a: string, b: string): boolean {
  if (a.trim() === '' || b.trim() === '') return a.trim() === b.trim();
  return Number(a) === Number(b);
}

/** Civil 'YYYY-MM-DD' → 'Oct 1, 2026' (UTC so the day never shifts). */
function formatDay(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Statements look a rate up on the 1st of their month, so a mid-month date first shows on the next one. */
function firstStatementMonth(iso: string): Date {
  const d = new Date(`${iso}T00:00:00Z`);
  if (d.getUTCDate() !== 1) d.setUTCMonth(d.getUTCMonth() + 1, 1);
  return d;
}

function lastMonthBefore(first: Date): Date {
  const d = new Date(first);
  d.setUTCMonth(d.getUTCMonth() - 1, 1);
  return d;
}

/** The API checks dates against UTC, so the earliest allowed day is today in UTC. */
function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfNextMonth(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString().slice(0, 10);
}

const METHOD_LABEL: Record<FeeCalculationMethod, string> = {
  FLAT: 'Flat',
  PER_UNIT: 'Per unit',
  PERCENTAGE: 'Percentage',
};

const GROUP_META: Record<string, { title: string; sub: string }> = {
  ACH: { title: 'ACH statement', sub: 'Monthly ACH statement' },
  CAMPAIGN: { title: 'Campaign statement', sub: 'SMS and email campaigns' },
  POS: { title: 'POS statement', sub: 'Monthly POS platform fees' },
};
const groupMeta = (type: string) => GROUP_META[type] ?? { title: `${type} statement`, sub: '' };
const groupLabel = (type: string) => groupMeta(type).title.replace(/ statement$/, '');

/** Only the ACH statement resolves a rate per merchant; the others are business-wide. */
const supportsMerchantOverride = (fee: TenantFee) => fee.statementType === 'ACH';

const GRID =
  'grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,2.1fr)_minmax(0,1.4fr)_2.5rem] items-center gap-x-4';

export interface FeeMerchantOption {
  merchantId: string;
  name: string;
}

// ── Card ──────────────────────────────────────────────────────────────────────

type DialogTarget = { fee: TenantFee; merchantId: string | null; newMerchant?: boolean };

/**
 * Super-admin "Fees & Pricing" rate card on the tenant detail page: the rates this
 * business's monthly statements charge, against the platform defaults. Changes are
 * effective-dated; statements for earlier months keep the rate they were issued with.
 */
export function TenantFeesCard({
  tenantId,
  merchants,
  heading = 'Fees & Pricing',
}: {
  tenantId: string;
  merchants: FeeMerchantOption[];
  /** Card title; the Fees & Pricing page passes the business name instead. */
  heading?: string;
}) {
  const { data, isLoading, isError, error, refetch } = useAdminTenantFees(tenantId);
  const save = useSaveTenantFeeChanges(tenantId);

  const [tab, setTab] = useState<string>('ALL');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [effectiveFrom, setEffectiveFrom] = useState(firstOfNextMonth);
  const [dialog, setDialog] = useState<DialogTarget | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const fees = useMemo(() => data?.data.fees ?? [], [data]);
  const merchantName = useMemo(() => new Map(merchants.map((m) => [m.merchantId, m.name])), [merchants]);

  const groups = useMemo(() => {
    const order: string[] = [];
    const byType = new Map<string, TenantFee[]>();
    for (const fee of fees) {
      if (!byType.has(fee.statementType)) {
        order.push(fee.statementType);
        byType.set(fee.statementType, []);
      }
      byType.get(fee.statementType)!.push(fee);
    }
    return order.map((type) => ({ type, fees: byType.get(type)! }));
  }, [fees]);

  const draftIds = Object.keys(drafts);
  const invalidDraft = draftIds.some((id) => drafts[id].trim() !== '' && !AMOUNT_RE.test(drafts[id].trim()));
  const replacesScheduled = draftIds.filter((id) => {
    const next = fees.find((f) => f.id === id)?.tenant.next;
    return next && next.effectiveFrom >= effectiveFrom;
  }).length;
  const firstMonth = monthLabel(firstStatementMonth(effectiveFrom));
  const keepMonth = monthLabel(lastMonthBefore(firstStatementMonth(effectiveFrom)));

  const setDraft = (fee: TenantFee, value: string) => {
    const original = fee.tenant.current ? inputAmount(fee.tenant.current.amount) : '';
    setDrafts((prev) => {
      const next = { ...prev };
      if (sameAmount(value, original)) delete next[fee.id];
      else next[fee.id] = value;
      return next;
    });
  };

  const saveDrafts = async () => {
    const changes: TenantFeeChange[] = draftIds.map((id) => ({
      feeTypeId: id,
      merchantId: null,
      amount: drafts[id].trim() === '' ? null : drafts[id].trim(),
    }));
    try {
      await save.mutateAsync({ effectiveFrom, changes });
      setDrafts({});
      toast.success(
        `${changes.length} rate${changes.length === 1 ? '' : 's'} saved · from ${formatDay(effectiveFrom)}`,
      );
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not save the rates'));
    }
  };

  const shown = tab === 'ALL' ? groups : groups.filter((g) => g.type === tab);

  return (
    <section aria-labelledby="tenant-fees-title" className="rounded-xl border border-border bg-white">
      <div className="flex flex-col gap-3 p-6 pb-4">
        <div className="flex flex-wrap items-center gap-3">
          <ReceiptText className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <h2 id="tenant-fees-title" className="text-lg font-semibold text-foreground">
            {heading}
          </h2>
          <span className="text-sm text-muted-foreground">tenant-wide</span>
          <span className="flex-1" />
          <Button variant="link" size="sm" className="px-0 text-sm" onClick={() => setHistoryOpen(true)}>
            <History className="h-4 w-4" aria-hidden="true" />
            View change history
          </Button>
        </div>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Rates printed on this business's monthly statements. Leave a rate empty to use the platform default. A change
          applies from the date you pick; earlier statements keep the rate they were issued with.
        </p>
        {groups.length > 1 && (
          <div
            role="tablist"
            aria-label="Statement type"
            className="flex flex-wrap gap-1 self-start rounded-lg bg-muted p-1"
          >
            {[
              { type: 'ALL', label: 'All' },
              ...groups.map((g) => ({ type: g.type, label: `${groupLabel(g.type)} · ${g.fees.length}` })),
            ].map((t) => (
              <button
                key={t.type}
                type="button"
                role="tab"
                aria-selected={tab === t.type}
                onClick={() => setTab(t.type)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  tab === t.type
                    ? 'bg-white font-semibold text-foreground shadow-sm'
                    : 'font-medium text-muted-foreground hover:text-foreground',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="border-t border-border px-6 py-10 text-center text-sm text-muted-foreground">
          Loading rates…
        </div>
      ) : isError ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-border px-6 py-6">
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            Couldn't load fees: {apiErrorMessage(error, 'request failed')}
          </div>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : groups.length === 0 ? (
        <div className="border-t border-border px-6 py-10 text-center text-sm text-muted-foreground">
          No fee types are set up yet.
        </div>
      ) : (
        <div className={cn('overflow-x-auto', draftIds.length === 0 && 'rounded-b-xl')}>
          <div className="min-w-[52rem]">
            {shown.map((g) => {
              const custom = g.fees.filter((f) => f.tenant.current).length;
              const scheduled = g.fees.filter((f) => f.tenant.next).length;
              const pending = g.fees.filter((f) => drafts[f.id] !== undefined).length;
              const meta = groupMeta(g.type);
              return (
                <div key={g.type}>
                  <div className="flex items-baseline gap-3 border-y border-border bg-muted/50 px-6 py-3">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">{meta.title}</h3>
                    {meta.sub && <span className="text-xs text-muted-foreground">{meta.sub}</span>}
                    <span className="flex-1" />
                    <span className="text-xs text-muted-foreground">
                      {custom} of {g.fees.length} customized
                      {scheduled > 0 && ` · ${scheduled} scheduled`}
                      {pending > 0 && ` · ${pending} change${pending === 1 ? '' : 's'} pending`}
                    </span>
                  </div>
                  <div
                    className={cn(
                      GRID,
                      'px-6 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground',
                    )}
                  >
                    <span>Fee</span>
                    <span>Charged</span>
                    <span className="text-right">Platform default</span>
                    <span>This business</span>
                    <span>In effect</span>
                    <span className="sr-only">Actions</span>
                  </div>
                  {g.fees.map((fee) => (
                    <FeeRow
                      key={fee.id}
                      fee={fee}
                      draft={drafts[fee.id]}
                      merchantName={merchantName}
                      onDraft={(v) => setDraft(fee, v)}
                      onEdit={(merchantId, newMerchant) => setDialog({ fee, merchantId, newMerchant })}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sticky offsets cancel the app shell's <main> padding (p-4 lg:p-6) so the bar
          sits on the visible bottom edge instead of floating above scrolled rows. */}
      {draftIds.length > 0 && (
        <div className="sticky -bottom-4 lg:-bottom-6 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-b-xl border-t border-border bg-white px-6 py-3">
          <span className="h-2 w-2 rounded-full bg-warning" aria-hidden="true" />
          <span className="text-sm font-semibold text-foreground">
            {draftIds.length} unsaved change{draftIds.length === 1 ? '' : 's'}
          </span>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span id="fees-effective-label">Takes effect from</span>
            <DateInput
              value={effectiveFrom}
              min={todayUtc()}
              onChange={(e) => e.target.value && setEffectiveFrom(e.target.value)}
              className="w-40"
              portal
            />
          </div>
          <span className="text-xs text-muted-foreground">
            First statement: {firstMonth} · {keepMonth} and earlier keep current rates
            {replacesScheduled > 0 &&
              ` · replaces ${replacesScheduled} scheduled change${replacesScheduled === 1 ? '' : 's'}`}
          </span>
          <span className="flex-1" />
          <Button variant="secondary" onClick={() => setDrafts({})} disabled={save.isPending}>
            Discard
          </Button>
          <Button onClick={saveDrafts} disabled={save.isPending || invalidDraft}>
            {save.isPending ? 'Saving…' : 'Save rates'}
          </Button>
        </div>
      )}

      {dialog && (
        <ChangeRateDialog
          key={`${dialog.fee.id}|${dialog.merchantId ?? ''}|${dialog.newMerchant ? 'new' : ''}`}
          tenantId={tenantId}
          target={dialog}
          merchants={merchants}
          onClose={() => setDialog(null)}
          onSaved={(feeId, merchantId) => {
            // A business-rate save supersedes an inline edit of the same fee.
            if (merchantId === null) {
              setDrafts((prev) => {
                const next = { ...prev };
                delete next[feeId];
                return next;
              });
            }
            setDialog(null);
          }}
        />
      )}

      <FeeHistoryModal
        tenantId={tenantId}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        merchantName={merchantName}
      />
    </section>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────

function NextChange({
  next,
  method,
  fallback,
}: {
  next: TenantFee['tenant']['next'];
  method: FeeCalculationMethod;
  fallback: string;
}) {
  if (!next) return null;
  return (
    <span className="flex items-center gap-1 text-xs font-medium text-info">
      <ArrowRight className="h-3 w-3 shrink-0" aria-hidden="true" />
      {next.amount === null ? fallback : formatRate(next.amount, method)} from {formatDay(next.effectiveFrom)}
    </span>
  );
}

function FeeRow({
  fee,
  draft,
  merchantName,
  onDraft,
  onEdit,
}: {
  fee: TenantFee;
  draft: string | undefined;
  merchantName: Map<string, string>;
  onDraft: (value: string) => void;
  onEdit: (merchantId: string | null, newMerchant?: boolean) => void;
}) {
  const current = fee.tenant.current;
  const edited = draft !== undefined;
  const value = edited ? draft : current ? inputAmount(current.amount) : '';
  const invalid = edited && draft.trim() !== '' && !AMOUNT_RE.test(draft.trim());
  const isPercent = fee.calculationMethod === 'PERCENTAGE';
  const businessRate = current ? formatRate(current.amount, fee.calculationMethod) : null;

  return (
    <div className={cn('border-t border-muted', edited && 'bg-warning/5')}>
      <div className={cn(GRID, 'px-6 py-3')}>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-foreground" title={fee.name}>
            {fee.name}
          </div>
          <div className="truncate font-mono text-xs text-muted-foreground">{fee.code}</div>
        </div>
        <div className="min-w-0 text-sm text-muted-foreground">
          <div>{METHOD_LABEL[fee.calculationMethod]}</div>
          {fee.description && (
            <div className="line-clamp-2 text-xs" title={fee.description}>
              {fee.description}
            </div>
          )}
        </div>
        <div className="text-right text-sm tabular-nums text-muted-foreground">
          {formatRate(fee.defaultAmount, fee.calculationMethod)}
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <label
            className={cn(
              'flex h-9 w-28 items-center gap-1 rounded-lg bg-white px-2.5 ring-1 ring-inset focus-within:ring-2',
              invalid ? 'ring-destructive' : edited ? 'ring-warning' : 'ring-input focus-within:ring-ring',
            )}
          >
            {!isPercent && <span className="text-sm text-muted-foreground">$</span>}
            <input
              aria-label={`${fee.name} rate for this business`}
              aria-invalid={invalid || undefined}
              inputMode="decimal"
              value={value}
              placeholder={inputAmount(fee.defaultAmount)}
              onChange={(e) => onDraft(e.target.value)}
              className={cn(
                'w-full min-w-0 bg-transparent text-right text-sm tabular-nums text-foreground outline-none placeholder:text-muted-foreground',
                (current || edited) && 'font-semibold',
              )}
            />
            {isPercent && <span className="text-sm text-muted-foreground">%</span>}
          </label>
          {edited ? (
            <Badge variant="warning">{draft.trim() === '' ? 'Reset' : 'Unsaved'}</Badge>
          ) : (
            current && <Badge variant="info">Custom</Badge>
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-0.5 text-sm text-muted-foreground">
          <span>{current ? `Since ${formatDay(current.effectiveFrom)}` : 'Platform default'}</span>
          <NextChange next={fee.tenant.next} method={fee.calculationMethod} fallback="Platform default" />
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Change rate for ${fee.name}`}
          title="Change rate"
          onClick={() => onEdit(null)}
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {supportsMerchantOverride(fee) && fee.merchants.length > 0 && (
        <div className="mx-6 mb-3 flex flex-col gap-2 rounded-lg border border-dashed border-border px-3.5 py-2.5 text-sm text-foreground">
          {fee.merchants.map((m) => (
            <div key={m.merchantId} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Store className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span className="font-semibold">Merchant override</span>
              <span className="text-muted-foreground">
                {m.merchantId}
                {merchantName.get(m.merchantId) && ` · ${merchantName.get(m.merchantId)}`}
                {' · '}
                {m.current
                  ? `${formatRate(m.current.amount, fee.calculationMethod)} since ${formatDay(m.current.effectiveFrom)}`
                  : 'Not started yet'}
              </span>
              <NextChange
                next={m.next}
                method={fee.calculationMethod}
                fallback={businessRate ? `business rate (${businessRate})` : 'platform default'}
              />
              <span className="flex-1" />
              <Button variant="link" size="sm" className="h-auto px-0" onClick={() => onEdit(m.merchantId)}>
                Edit
              </Button>
            </div>
          ))}
          <div>
            <Button variant="link" size="sm" className="h-auto px-0" onClick={() => onEdit(null, true)}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add merchant override
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Change rate dialog ────────────────────────────────────────────────────────

function ChangeRateDialog({
  tenantId,
  target,
  merchants,
  onClose,
  onSaved,
}: {
  tenantId: string;
  target: DialogTarget;
  merchants: FeeMerchantOption[];
  onClose: () => void;
  onSaved: (feeId: string, merchantId: string | null) => void;
}) {
  const { fee } = target;
  const save = useSaveTenantFeeChanges(tenantId);
  const canOverride = supportsMerchantOverride(fee) && merchants.length > 0;

  const [scope, setScope] = useState<'business' | 'merchant'>(
    target.merchantId || target.newMerchant ? 'merchant' : 'business',
  );
  const [merchantId, setMerchantId] = useState(target.merchantId ?? '');
  const rateFor = (s: 'business' | 'merchant', m: string) =>
    s === 'business' ? fee.tenant : (fee.merchants.find((x) => x.merchantId === m) ?? { current: null, next: null });
  const [amount, setAmount] = useState(() => {
    const cur = rateFor(scope, merchantId).current;
    return cur ? inputAmount(cur.amount) : '';
  });
  const [effectiveFrom, setEffectiveFrom] = useState(firstOfNextMonth);

  const rate = rateFor(scope, merchantId);
  const method = fee.calculationMethod;
  const platformDefault = formatRate(fee.defaultAmount, method);
  const businessRate = fee.tenant.current ? formatRate(fee.tenant.current.amount, method) : platformDefault;
  // What this scope is charged when it has no rate of its own.
  const fallback = scope === 'business' ? platformDefault : businessRate;
  const nowRate = rate.current ? formatRate(rate.current.amount, method) : fallback;
  const nowNote = rate.current ? '' : scope === 'business' ? ' (platform default)' : ' (business rate)';
  const scopeName =
    scope === 'merchant' ? (merchants.find((m) => m.merchantId === merchantId)?.name ?? merchantId) : '';

  const trimmed = amount.trim();
  const amountValid = AMOUNT_RE.test(trimmed);
  const needsMerchant = scope === 'merchant' && !merchantId;
  const first = firstStatementMonth(effectiveFrom);
  const replaces = rate.next && rate.next.effectiveFrom >= effectiveFrom;
  // Re-scheduling the rate already in force only adds noise, unless it cancels a pending change.
  const unchanged = !!rate.current && sameAmount(trimmed, inputAmount(rate.current.amount)) && !rate.next;

  const switchScope = (s: 'business' | 'merchant', m: string) => {
    setScope(s);
    setMerchantId(m);
    const cur = rateFor(s, m).current;
    setAmount(cur ? inputAmount(cur.amount) : '');
  };

  const submit = async (newAmount: string | null) => {
    const scopedMerchant = scope === 'merchant' ? merchantId : null;
    try {
      await save.mutateAsync({
        effectiveFrom,
        changes: [{ feeTypeId: fee.id, merchantId: scopedMerchant, amount: newAmount }],
      });
      const who = scope === 'merchant' ? ` for ${scopeName}` : '';
      toast.success(
        newAmount === null
          ? `${fee.name}${who} returns to ${scope === 'business' ? 'the platform default' : 'the business rate'} from ${formatDay(effectiveFrom)}`
          : `${fee.name}${who} set to ${formatRate(newAmount, method)} from ${formatDay(effectiveFrom)}`,
      );
      onSaved(fee.id, scopedMerchant);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not save the rate'));
    }
  };

  const hasOwnRate = !!(rate.current || rate.next);

  return (
    <Modal
      open
      onClose={onClose}
      title="Change rate"
      subtitle={`${fee.name} · ${groupMeta(fee.statementType).title} · ${METHOD_LABEL[method].toLowerCase()}`}
      widthClass="max-w-[600px]"
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          {hasOwnRate && (
            <Button
              variant="link"
              className="px-0"
              disabled={save.isPending || needsMerchant}
              onClick={() => submit(null)}
            >
              {scope === 'business' ? 'Reset to platform default' : 'Remove override'}
            </Button>
          )}
          <span className="flex-1" />
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => submit(trimmed)}
            disabled={save.isPending || !amountValid || needsMerchant || unchanged}
          >
            {save.isPending ? 'Saving…' : amountValid ? `Schedule ${formatRate(trimmed, method)}` : 'Schedule'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {supportsMerchantOverride(fee) && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-sm font-semibold text-foreground">Applies to</legend>
            <label
              className={cn(
                'flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm ring-1 ring-inset',
                scope === 'business' ? 'bg-primary/5 ring-primary' : 'ring-border',
              )}
            >
              <input
                type="radio"
                name="fee-scope"
                checked={scope === 'business'}
                onChange={() => switchScope('business', '')}
                className="h-4 w-4 shrink-0 accent-primary"
              />
              <span>
                <span className="font-semibold">Whole business</span>
                <span className="text-muted-foreground"> · every ACH merchant without its own override</span>
              </span>
            </label>
            <div
              className={cn(
                'flex flex-col gap-2 rounded-lg px-3 py-2.5 text-sm ring-1 ring-inset',
                scope === 'merchant' ? 'bg-primary/5 ring-primary' : 'ring-border',
                !canOverride && 'opacity-60',
              )}
            >
              <label className={cn('flex items-center gap-2.5', canOverride ? 'cursor-pointer' : 'cursor-not-allowed')}>
                <input
                  type="radio"
                  name="fee-scope"
                  checked={scope === 'merchant'}
                  disabled={!canOverride}
                  onChange={() => switchScope('merchant', merchantId)}
                  className="h-4 w-4 shrink-0 accent-primary"
                />
                <span>
                  <span className="font-semibold">One ACH merchant</span>
                  <span className="text-muted-foreground">
                    {canOverride ? ' · ACH statement only' : ' · no locations have a merchant number yet'}
                  </span>
                </span>
              </label>
              {scope === 'merchant' && (
                <select
                  aria-label="Merchant"
                  value={merchantId}
                  onChange={(e) => switchScope('merchant', e.target.value)}
                  className="h-9 w-full rounded-lg bg-white px-2 text-sm outline-none ring-1 ring-inset ring-input focus:ring-2 focus:ring-ring"
                >
                  <option value="">Choose a location…</option>
                  {merchants.map((m) => (
                    <option key={m.merchantId} value={m.merchantId}>
                      {m.name} · {m.merchantId}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </fieldset>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-foreground">
            New rate
            <span
              className={cn(
                'flex h-10 items-center gap-1.5 rounded-lg bg-white px-3 font-normal ring-1 ring-inset focus-within:ring-2',
                trimmed !== '' && !amountValid ? 'ring-destructive' : 'ring-input focus-within:ring-ring',
              )}
            >
              {method !== 'PERCENTAGE' && <span className="text-muted-foreground">$</span>}
              <input
                aria-label="New rate"
                value={amount}
                inputMode="decimal"
                aria-invalid={(trimmed !== '' && !amountValid) || undefined}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full min-w-0 bg-transparent tabular-nums outline-none"
              />
              {method === 'PERCENTAGE' && <span className="text-muted-foreground">%</span>}
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              Now {nowRate}
              {nowNote || ` · platform default ${platformDefault}`}
            </span>
          </label>
          <div className="flex flex-col gap-1.5 text-sm font-semibold text-foreground">
            <span>Effective from</span>
            <DateInput
              value={effectiveFrom}
              min={todayUtc()}
              onChange={(e) => e.target.value && setEffectiveFrom(e.target.value)}
              portal
            />
            <span className="text-xs font-normal text-muted-foreground">Defaults to the 1st of next month</span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-muted/50 px-3.5 py-3 text-sm">
          <div className="font-semibold text-foreground">What statements will show</div>
          <div className="flex justify-between gap-4 text-muted-foreground">
            <span>{monthLabel(lastMonthBefore(first))} and earlier</span>
            <span className="tabular-nums text-foreground">{nowRate}</span>
          </div>
          <div className="flex justify-between gap-4 text-muted-foreground">
            <span>{monthLabel(first)} onward</span>
            <span className="font-semibold tabular-nums text-foreground">
              {amountValid ? formatRate(trimmed, method) : '—'}
            </span>
          </div>
          {replaces && rate.next && (
            <div className="text-xs text-amber-700">
              Replaces the change already scheduled for {formatDay(rate.next.effectiveFrom)}.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── History ───────────────────────────────────────────────────────────────────

function FeeHistoryModal({
  tenantId,
  open,
  onClose,
  merchantName,
}: {
  tenantId: string;
  open: boolean;
  onClose: () => void;
  merchantName: Map<string, string>;
}) {
  const { data, isLoading, isError, error } = useAdminTenantFeeHistory(tenantId, open);
  const rows = data?.data ?? [];
  const today = todayUtc();

  const status = (r: (typeof rows)[number]) => {
    if (r.status === 'INACTIVE') return { label: 'Cancelled', variant: 'default' as const };
    if (r.effectiveTo && r.effectiveTo < today) return { label: 'Ended', variant: 'default' as const };
    if (r.effectiveFrom > today) return { label: 'Scheduled', variant: 'info' as const };
    return { label: 'In effect', variant: 'success' as const };
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Fee change history"
      subtitle="Every rate set for this business, newest first. Cancelled = replaced before it took effect."
      widthClass="max-w-5xl"
    >
      {isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading history…</div>
      ) : isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn't load history: {apiErrorMessage(error, 'request failed')}
        </div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No rates have been set for this business. Every fee uses the platform default.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-4 font-semibold">Changed</th>
                <th className="py-2 pr-4 font-semibold">Fee</th>
                <th className="py-2 pr-4 font-semibold">Applies to</th>
                <th className="py-2 pr-4 text-right font-semibold">Rate</th>
                <th className="py-2 pr-4 font-semibold">Effective</th>
                <th className="py-2 pr-4 font-semibold">Status</th>
                <th className="py-2 font-semibold">By</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = status(r);
                return (
                  <tr key={r.id} className="border-b border-muted align-top">
                    <td className="whitespace-nowrap py-2 pr-4 text-muted-foreground">{formatDateTime(r.createdAt)}</td>
                    <td className="py-2 pr-4">
                      <div className="font-medium text-foreground">{r.feeName}</div>
                      <div className="font-mono text-xs text-muted-foreground">{r.feeCode}</div>
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {r.merchantId ? (
                        <>
                          <div>{merchantName.get(r.merchantId) ?? 'Merchant'}</div>
                          <div className="font-mono text-xs">{r.merchantId}</div>
                        </>
                      ) : (
                        'Whole business'
                      )}
                    </td>
                    <td className="whitespace-nowrap py-2 pr-4 text-right tabular-nums text-foreground">
                      {formatRate(r.amount, r.calculationMethod)}
                    </td>
                    <td className="whitespace-nowrap py-2 pr-4 text-muted-foreground">
                      {formatDay(r.effectiveFrom)}
                      {r.effectiveTo ? ` – ${formatDay(r.effectiveTo)}` : ' onward'}
                    </td>
                    <td className="py-2 pr-4">
                      <Badge variant={s.variant}>{s.label}</Badge>
                    </td>
                    <td className="whitespace-nowrap py-2 text-muted-foreground">{r.createdBy ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
